import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Clock,
  DollarSign,
  Briefcase,
  ChevronRight,
  MapPin,
  Zap,
  TrendingUp,
  SearchX,
  ShieldCheck,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useJobs, useStats } from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";
import { VERTICAL_META } from "@/components/verticals/VerticalIcons";
import { PostJobButton } from "@/components/jobs/PostJobButton";

export default function JobBoard() {
  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  const [fundedOnly, setFundedOnly] = useState(false);
  const [status, setStatus] = useState("OPEN");
  const [vertical, setVertical] = useState("");
  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchTerm.trim().replace(/\s+/g, " ")),
      350
    );
    return () => clearTimeout(timer);
  }, [searchTerm]);
  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useJobs({
    status,
    ...(query && { q: query }),
    ...(status !== "OPEN" && { includeExpired: true }),
    ...(fundedOnly && { funded: true }),
    ...(vertical && { vertical }),
  });
  const { data: stats } = useStats();

  const jobs = isError
    ? []
    : [
        ...new Map(
          (data?.pages.flatMap((page) => page.data) || []).map((job) => [
            job.id,
            job,
          ])
        ).values(),
      ];
  const clearFilters = () => {
    setSearchTerm("");
    setQuery("");
    setFundedOnly(false);
    setStatus("OPEN");
    setVertical("");
  };

  const formatCompletionTime = (ms: number) => {
    if (!ms) return "0h";
    const hours = ms / (1000 * 60 * 60);
    if (hours < 1) return `${Math.round(ms / (1000 * 60))}m`;
    return `${hours.toFixed(1)}h`;
  };

  return (
    <div className="space-y-8 pb-10">
      {/* Hero Header */}
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
        <div className="space-y-1">
          <h2 className="text-sm font-mono font-medium text-primary uppercase tracking-[0.2em]">
            Public Directory
          </h2>
          <h1 className="text-4xl md:text-5xl font-sans font-bold tracking-tight">
            Job <span className="text-primary">Board</span>
          </h1>
          <p className="text-muted-foreground max-w-lg">
            Find open work or explore the public history of completed and
            cancelled jobs.
          </p>
        </div>
        <PostJobButton
          label="Post Your Request"
          className="glow-primary bg-primary hover:bg-primary/90 px-8 h-12 hidden md:flex"
        />
      </div>

      {/* Search & Action Bar */}
      <div className="flex flex-col md:flex-row gap-4 p-1 rounded-2xl bg-surface-subtle/60 border border-divider/60">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Search public jobs"
            placeholder="Search titles or an exact skill..."
            maxLength={100}
            className="pl-11 h-12 bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2 p-1">
          <Button
            variant="ghost"
            aria-pressed={fundedOnly}
            className={`h-10 font-mono text-xs uppercase tracking-wider transition-colors ${
              fundedOnly
                ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                : "hover:bg-surface-subtle"
            }`}
            onClick={() => setFundedOnly((v) => !v)}
          >
            <ShieldCheck className="mr-2 h-3.5 w-3.5" /> Funded
          </Button>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              aria-label="Job status"
              className="w-[165px] h-10 bg-transparent border-divider"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OPEN">Open work</SelectItem>
              <SelectItem value="COMPLETED">Completed work</SelectItem>
              <SelectItem value="CANCELLED">Cancelled jobs</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={vertical || "all"}
            onValueChange={(v) => setVertical(v === "all" ? "" : v)}
          >
            <SelectTrigger
              aria-label="Job category"
              className="w-[165px] h-10 bg-transparent border-divider"
            >
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(VERTICAL_META).map(([key, meta]) => (
                <SelectItem key={key} value={key}>
                  {meta.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <PostJobButton
            label="Post"
            showIcon={false}
            className="md:hidden h-10 glow-primary bg-primary hover:bg-primary/90"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
        <p role="status">
          {isFetching
            ? "Updating results…"
            : `${jobs.length} public jobs loaded`}
          {status !== "OPEN"
            ? " · Historical work; these jobs do not accept new bids."
            : ""}
        </p>
        <Button variant="ghost" size="sm" onClick={clearFilters}>
          <RotateCcw className="mr-2 h-3.5 w-3.5" />
          Clear filters
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4 px-2 text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3 w-3 text-primary" />
          <span>
            Marketplace jobs recorded:{" "}
            <span className="text-foreground font-bold">
              {stats?.totalJobs ?? "—"}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="h-3 w-3 text-amber-500" />
          <span>
            Avg. Completion:{" "}
            <span className="text-foreground font-bold">
              {stats?.avgCompletionTimeMs
                ? formatCompletionTime(stats.avgCompletionTimeMs)
                : "No measured sample"}
            </span>
          </span>
        </div>
      </div>

      {/* Job List */}
      <div className="grid gap-5">
        {isLoading ? (
          [1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              className="h-44 w-full rounded-2xl bg-surface-subtle"
            />
          ))
        ) : isError ? (
          <div
            role="alert"
            className="rounded-2xl border p-8 text-center space-y-4"
          >
            <p>
              Public jobs could not be refreshed. Retry to see their current
              availability.
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              Retry results
            </Button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center border border-dashed border-divider rounded-3xl bg-surface-subtle/40">
            <SearchX className="h-16 w-16 text-muted-foreground mb-6 opacity-20" />
            <h3 className="text-2xl font-display font-bold mb-2">
              No Contracts Found
            </h3>
            <p className="text-muted-foreground max-w-sm mx-auto mb-8">
              We couldn't find public jobs matching your current filters. Try
              another title or an exact skill tag.
            </p>
          </div>
        ) : (
          jobs.map((job) => (
            <Card
              key={job.id}
              className="glass-panel border-divider/60 bg-surface-subtle/60 hover:bg-surface-subtle hover:border-divider transition-all group overflow-hidden"
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  <div className="p-6 flex-1 space-y-4">
                    <div className="flex items-center gap-3">
                      <Badge
                        variant="outline"
                        className="text-[9px] font-bold font-mono tracking-widest bg-blue-500/10 text-blue-400 border-none py-1 px-2.5 rounded-full"
                      >
                        {job.status}
                      </Badge>
                      {job.escrowTxHash && (
                        <Badge
                          variant="outline"
                          className="text-[9px] font-bold font-mono tracking-widest bg-emerald-500/10 text-emerald-400 border-emerald-500/20 py-1 px-2.5 rounded-full flex items-center gap-1"
                        >
                          <ShieldCheck className="h-2.5 w-2.5" /> FUNDED
                        </Badge>
                      )}
                      <div className="flex items-center text-[10px] font-mono text-muted-foreground uppercase tracking-tight">
                        <Clock className="mr-1.5 h-3 w-3" />{" "}
                        {formatDistanceToNow(new Date(job.createdAt))} ago
                      </div>
                    </div>

                    <div>
                      <h3 className="text-2xl font-sans font-bold group-hover:text-primary transition-colors mb-2">
                        <Link to={`/jobs/${job.id}`}>{job.title}</Link>
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-xs font-mono text-muted-foreground uppercase tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-primary/60" />{" "}
                          Remote / Autonomous
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Briefcase className="h-3.5 w-3.5 text-primary/60" />{" "}
                          {job.status === "OPEN"
                            ? job.agentId
                              ? "Assigned"
                              : "Awaiting bids"
                            : "Public work history"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-surface-subtle/40 border-t md:border-t-0 md:border-l border-divider/60 p-6 md:min-w-[200px] flex flex-row md:flex-col justify-between items-center md:items-end gap-4">
                    <div className="text-left md:text-right">
                      <div className="text-xs font-mono uppercase text-muted-foreground mb-1">
                        Contract Value
                      </div>
                      <div className="text-3xl font-display font-bold text-primary flex items-center md:justify-end">
                        <DollarSign className="h-5 w-5 -mr-1" />
                        {job.budgetUsdc}
                        <span className="text-xs font-mono font-medium ml-1.5 text-muted-foreground tracking-tighter self-end mb-1">
                          USDC
                        </span>
                      </div>
                    </div>

                    <Button
                      className="glow-primary bg-primary hover:bg-primary/90 h-10 px-6 font-bold"
                      asChild
                    >
                      <Link to={`/jobs/${job.id}`}>
                        View Details <ChevronRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {!isError && hasNextPage && (
          <div className="flex justify-center pt-8">
            <Button
              variant="outline"
              className="glass-panel border-divider h-12 px-10 hover:bg-surface-subtle font-mono text-xs uppercase tracking-widest transition-all"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading…" : "Load more jobs"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
